import React from "react";
import styled, { css } from "styled-components";
import showdown from "showdown";
import { AIChatResponseDto } from "../../model/chat-wiget/dto/AIChatResponseDto";

/**
 * Shared across renders: the options are static, and the defaults break the
 * markdown Fahmy actually writes.
 * - disableForced4SpacesIndentedSublists: sub-lists are indented with 2-3
 *   spaces, not showdown's required 4, so nested steps used to leak out as
 *   literal "1." text instead of becoming a nested <ol>.
 * - smartIndentationFix / simpleLineBreaks: keep single newlines as breaks
 *   rather than gluing separate steps into one paragraph.
 */
const converter = new showdown.Converter({
  disableForced4SpacesIndentedSublists: true,
  smartIndentationFix: true,
  simpleLineBreaks: true,
  simplifiedAutoLink: false,
  tables: false,
});

type AIFormMessageProps = {
  message: AIChatResponseDto;
};

const MessageWrapper = styled.div<{ isUser: boolean; dir: "ltr" | "rtl" }>`
  border-radius: 0.5rem;
  padding: 0.375rem 0.75rem;
  margin-bottom: 0.5rem;
  max-width: 85%;
  font-size: 0.875rem;
  line-height: 1.4;
  position: relative;
  z-index: 1;

  ${({ isUser, theme }) =>
    isUser
      ? css`
          margin-left: auto;
          text-align: right;
          color: ${theme.main};
          &::before {
            content: "";
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            border-radius: inherit;
            opacity: 0.2;
            background: ${theme.main};
            pointer-events: none;
            z-index: -1;
          }
        `
      : css`
          margin-right: auto;
          text-align: left;
          background: #e5e7eb;
          color: #374151;
        `}

  text-align: ${(props) => (props.dir === "rtl" ? "right" : "left")};
`;

const HtmlContent = styled.div`
  & * {
    font-size: revert;
    font-weight: revert;
  }
  a {
    color: ${({ theme }) => theme.main};
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.2s;
    &:hover {
      color: #2563eb;
    }
  }

  /*
   * Long answers are stages with steps inside them, so lists must stay
   * vertical and keep their own numbering per level. Outside positioning
   * plus a padding on the inline start indents nested levels and keeps
   * wrapped lines aligned in both LTR and RTL.
   */
  p {
    margin-block: 0.25rem;
  }

  ul,
  ol {
    margin-block: 0.25rem;
    padding-inline-start: 1.25rem;
    list-style-position: outside;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  ul ul {
    list-style-type: circle;
  }

  /* QA asked for nested steps to read 1. 2., not a. b. */
  ol ol {
    list-style-type: decimal;
  }

  li {
    margin-block: 0.25rem;
  }

  li > p {
    margin-block: 0;
  }

  li > ul,
  li > ol {
    margin-top: 0.25rem;
  }

  h1,
  h2,
  h3 {
    margin-block: 0.75rem 0.25rem;
    font-weight: bold;
  }

  h1 {
    font-size: 1rem;
  }

  h2,
  h3 {
    font-size: 0.875rem;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;

export const AIFormMessage: React.FC<AIFormMessageProps> = ({ message }) => {
  const { question, answer, by } = message || {};
  const isUser = by === "user";
  const fullText = question || answer || "";

  const detectTextDirection = (text: string) => {
    if (!text) {
      return "ltr";
    }

    const cleaned = text.replace(/[\s\p{P}\p{S}\p{N}]+/gu, "");
    const rtlRegex = /[\u0590-\u08FF]/;
    return rtlRegex.test(cleaned) ? "rtl" : "ltr";
  };

  const escapeHtml = (text: string) => {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const convertMarkdownLinks = (text: string) => {
    if (!text) {
      return "";
    }

    const markdownRegex =
      /\[([^\]]+)\]\((https?:\/\/|mailto:|tel:|sms:|www\.)[^\s)]+\)/g;

    return text.replace(markdownRegex, (match) => {
      const parts = match.match(/\[([^\]]+)\]\((.+)\)/);
      if (!parts) {
        return match;
      }

      let label = escapeHtml(parts[1]);
      let url = parts[2];

      if (url.startsWith("www.")) {
        url = `http://${url}`;
      }

      const isExternal =
        url.startsWith("http://") || url.startsWith("https://");
      const extraAttrs = isExternal
        ? ' target="_blank" rel="noopener noreferrer"'
        : "";

      return `<a href="${url}"${extraAttrs} dir="${detectTextDirection(label)}">${label}</a>`;
    });
  };

  const dir = detectTextDirection(fullText);

  return (
    <MessageWrapper isUser={isUser} dir={dir}>
      {isUser ? (
        fullText
      ) : (
        <HtmlContent
          dangerouslySetInnerHTML={{
            __html: converter.makeHtml(convertMarkdownLinks(fullText)),
          }}
        />
      )}
    </MessageWrapper>
  );
};
